import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { routes } from '@/router'
import {
  useCreatePartnerMonthlyWithdraw,
  useListPartners,
  useUpdateDocumentRequestStatus,
} from '@/api/accounting-dealer'
import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { FormProvider, useForm } from 'react-hook-form'
import { MonthlyDocumentsData, monthlyDocumentsSchema } from './schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from '@/helpers'
import { CompanyDocuments } from './company-documents'
import { useCompany } from '@/context/companies'
import { PartnerDocuments } from './partner-documents'
import { DocumentRequest } from '@/types'

type DocumentsFormProps = {
  documentRequest: DocumentRequest
}

export function DocumentsForm({ documentRequest }: DocumentsFormProps) {
  const { company } = useCompany()

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const { data: partners } = useListPartners({
    companyKey: company.key,
  })

  const {
    mutateAsync: updateDocumentRequestStatus,
    isPending: updatingDocumentRequest,
  } = useUpdateDocumentRequestStatus({
    onSuccess: () => {
      navigate(routes.accounting.monthlyDocuments)
      queryClient.invalidateQueries({
        queryKey: ['document-request-list', company.key],
      })
    },
  })

  const {
    mutateAsync: createPartnerMonthlyWithdraw,
    isPending: creatingPartnerMonthlyWithdraw,
  } = useCreatePartnerMonthlyWithdraw()

  const form = useForm<MonthlyDocumentsData>({
    resolver: zodResolver(monthlyDocumentsSchema),
    values: {
      observation: documentRequest.observation,
      no_financial_movement: false,
      company: {
        bank_statement_pdf: [],
        bank_statement: [],
        bank_investment_statement: [],
        other: [],
      },
      partners:
        partners?.map((partner) => ({
          key: partner.key,
          payslip: [],
          monthly_withdraw: '',
        })) || [],
    },
    resetOptions: {
      keepDirtyValues: true,
    },
  })

  const { watch } = form

  const noFinancialMovement = watch('no_financial_movement')

  const handleSendDocuments = async ({
    no_financial_movement,
    company: { bank_statement_pdf },
    partners,
    observation,
  }: MonthlyDocumentsData) => {
    if (no_financial_movement === false && bank_statement_pdf?.length === 0) {
      toast.error('Extrato bancário é obrigatório')
      return
    }

    try {
      await updateDocumentRequestStatus({
        status: 'pending_review',
        document_request_key: documentRequest.key,
        observation,
        no_financial_movement,
      })

      await Promise.all(
        partners.map(async (partner) => {
          await createPartnerMonthlyWithdraw({
            companyKey: company.key,
            partnerKey: partner.key,
            data: {
              month: parseInt(documentRequest.month),
              year: parseInt(documentRequest.year),
              amount: parseFloat(partner.monthly_withdraw || '0'),
            },
          })
        }),
      )
    } catch (error) {
      console.error(error)
      toast.error('Erro ao enviar documentos')
    }
  }

  return (
    <FormProvider {...form}>
      <Stack
        spacing={3}
        component="form"
        onSubmit={form.handleSubmit(handleSendDocuments)}
        sx={{
          pb: 12,
        }}
      >
        <FormControlLabel
          control={<Switch {...form.register('no_financial_movement')} />}
          label="Não houve movimentação financeira esse mês"
        />

        {!noFinancialMovement && (
          <Box>
            <Typography variant="h5" mb={1}>
              PJ - {company.corporate_name || 'N/D'}
            </Typography>

            <CompanyDocuments
              companyKey={company.key}
              documentRequestKey={documentRequest.key}
            />
          </Box>
        )}

        {partners?.map((partner, index) => (
          <Box key={partner.key}>
            <Typography variant="h5" mb={1}>
              PF - {partner.name}
            </Typography>

            <PartnerDocuments
              partnerIndex={index}
              partnerKey={partner.key}
              documentRequest={documentRequest}
              partnerName={partner.name}
            />
          </Box>
        ))}

        <Box>
          <Typography variant="h5" mb={1}>
            Observação
          </Typography>
          <Typography variant="body1" mb={1}>
            Caso necessário, insira uma observação para os documentos enviados.
          </Typography>
          <TextField
            {...form.register('observation')}
            label="Observação (Opcional)"
            multiline
            size="small"
            rows={4}
          />
        </Box>

        <LoadingButton
          sx={{ alignSelf: ['auto', 'end'] }}
          loading={updatingDocumentRequest || creatingPartnerMonthlyWithdraw}
          type="submit"
        >
          Finalizar
        </LoadingButton>
      </Stack>
    </FormProvider>
  )
}
